import {
	createFileRoute,
	useCanGoBack,
	useNavigate,
	useRouteContext,
	useRouter,
} from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
	ConnectionHandler,
	graphql,
	useLazyLoadQuery,
	useMutation,
} from "react-relay";
import { toast } from "sonner";
import { Heading } from "@/components/heading";
import { Card } from "@/components/ui/card";
import { EventForm, type EventFormSchemaValues } from "@/forms/event-form";
import type { eventos_adminQuery } from "./__generated__/eventos_adminQuery.graphql";
import type { eventosCreateMutation } from "./__generated__/eventosCreateMutation.graphql";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";

export const Route = createFileRoute("/_visibility/_topnav/eventos/novo")({
	component: RouteComponent,
});

function RouteComponent() {
	const router = useRouter();
	const canGoBack = useCanGoBack();
	const navigate = useNavigate({ from: Route.fullPath });
	const { club } = useRouteContext({ from: "__root__" });

	const data = useLazyLoadQuery<eventos_adminQuery>(
		graphql`query eventos_adminQuery($club: String!) {
				club(slug: $club) {
				  id
					offers(first: 10000) {
					  edges {
					    node {
    					  id
    					  productName
					    }
					  }
					}
					admins(first: 10000) {
						edges {
							node {
		            id
		            displayName
		            avatarFallback
		            photo {
		              signedUrl
		            }
							}
						}
					}
					tags(first: 10000) {
					  edges {
						  node {
								id
								text
							}
						}
					}
					badges(first: 10000) {
					  edges {
							node {
					      id
								...memberBadgeFragment
							}
						}
					}
				}
		}`,
		{ club: club.slug },
	);

	const [createEvent] = useMutation<eventosCreateMutation>(graphql`
	   mutation eventosCreateMutation($club: String!, $payload: CreateEventInput!) {
	       createEvent(payload: $payload, club: $club) {
	           eventEdge {
	               node {
	                 id
	               }
	           }
	       }
	   }
	 `);

	const availableHosts = data.club.admins?.edges?.map((ed) => ed.node);

	const [isPending, setIsPending] = useState(false);
	const { track } = useAnalytics();

	const onSubmit = (value: EventFormSchemaValues) => {
		setIsPending(true);
		return createEvent({
			variables: {
				club: club.slug,
				payload: {
					title: value.title,
					description: JSON.stringify(value.description),
					host: value.host,
					visibility: value.visibility,
					startDate: value.startDate,
					endDate: value.endDate,
					eventType: value.type,
					url: value.type === "LIVE" ? value.url : null,
					address: value.type === "IN_PERSON" ? value.address : null,
					badges: value.badges,
					tags: value.tags,
					externalProducts: value.products,
					sendPromotionEmail: value.sendPromotionEmail,
					sendConfirmationEmail: value.sendConfirmationEmail,
					sendReminderEmail: value.sendReminderEmail,
				},
			},
			onCompleted: () => {
				track(AnalyticsEvents.EVENT_CREATED, {
					event_type: value.type,
					visibility: value.visibility,
				});
				toast.success("Evento criado com sucesso!");

				if (canGoBack) {
					router.history.back();
				} else {
					navigate({ to: "/eventos" });
				}
			},
			onError: () => {
				setIsPending(false);
				toast.error(
					"Não foi possível criar o evento no momento. Verifique os dados preenchidos e tente novamente.",
				);
			},
			updater: (store) => {
				const clubRecord = store.get(data.club.id);

				if (clubRecord) {
					const filters = ["UPCOMING", "PAST"];
					const orderBys = ["CHRONOLOGICAL", "REVERSE"];

					filters.forEach((f) => {
						orderBys.forEach((o) => {
							const connection = ConnectionHandler.getConnection(
								clubRecord,
								"eventosFeedFragment__eventsByMonth",
								{ orderBy: o, filter: f },
							);

							connection?.invalidateRecord();
						});
					});
				}
			},
		});
	};

	const availableProducts = useMemo(() => {
		return (
			data?.club?.offers?.edges?.flatMap(({ node: product }) => {
				return product;
			}) || []
		);
	}, [data?.club?.offers]);

	const availableBadges = useMemo(() => {
		return data?.club?.badges?.edges?.map((edge) => edge.node) || [];
	}, [data?.club?.badges]);

	const availableTags = useMemo(() => {
		return data?.club?.tags?.edges?.map((edge) => edge.node) || [];
	}, [data?.club?.tags]);

	return (
		<div className="size-full flex items-center justify-center py-4 md:py-8">
			<Card className="p-6 w-full max-md:px-4 max-w-[700px] flex flex-col gap-6">
				<Heading>Criar Evento</Heading>

				<EventForm
					onSubmit={onSubmit}
					products={availableProducts}
					tags={availableTags}
					badges={availableBadges}
					hosts={availableHosts}
					isPending={isPending}
				/>
			</Card>
		</div>
	);
}
